import { useCallback } from "react";
import { container } from "tsyringe";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import { TextArea } from "components/materials/Form/TextAreaElement";
import I18n from "components/materials/I18n";
import UpcomingActionRequestResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionRequestResource";
import UpcomingActionResponseResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionResponseResource";
import { useProjectId } from "hooks/useProjectId";
import UpcomingActionService from "services/projects/UpcomingActionService";
import { utcToZonedTime } from "utils/DateUtils";
import { translateISO369 } from "utils/I18nText";
import { useActionInfo } from "../../ActionCard";
import classes from "./classes.module.scss";

type IProps = {
	action: UpcomingActionResponseResource;
	isOpen: boolean;
	onClose: () => void;
	onEdited: (action: UpcomingActionResponseResource) => void;
};

const upcomingActionService = container.resolve(UpcomingActionService);

export default function EditActionModal(props: IProps) {
	const { formRef, errors, handleError, clearErrors } = useForm();
	const { projectId } = useProjectId();
	const { imageFile: defaultActionImage } = useActionInfo(props.action);

	const onEditAction = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const upcomingActionRequestResource = UpcomingActionRequestResource.hydrate<UpcomingActionRequestResource>({
				title: [{ lang: I18n.getLang(), text: formData["title"] as string }],
				description: [{ lang: I18n.getLang(), text: formData["description"] as string }],
				buttonLabel: formData["buttonLabel"] ? [{ lang: I18n.getLang(), text: formData["buttonLabel"] as string }] : undefined,
				buttonLink: formData["buttonLink"] ? (formData["buttonLink"] as string) : undefined,
				startsAt: new Date(formData["startsAt"] as string),
				endsAt: new Date(formData["endsAt"] as string),
			});

			const image = (formData["image"] as FileList)?.[0];

			upcomingActionRequestResource
				.validateOrReject()
				.then(() => {
					upcomingActionService.update(projectId, props.action.id, upcomingActionRequestResource, image).then(props.onEdited).catch(handleError);
				})
				.catch(handleError);
		},
		[clearErrors, handleError, projectId, props],
	);

	return (
		<Modal isOpen onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onEditAction} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<Input
						name="title"
						required
						additionalInfo={I18n.asset.pages.professional_space.tabs.actions.create_action_modal.action_title_description}
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_title}
						defaultValue={props.action.title ? translateISO369(props.action.title) : null}
						className={classes["input"]}
					/>
					<TextArea
						name="description"
						required
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_description}
						defaultValue={translateISO369(props.action.description) ?? undefined}
						className={classes["input"]}
					/>
					<DragAndDropFile
						name="image"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_image}
						description={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_image_description}
						formRef={formRef}
						defaultFile={defaultActionImage}
						className={classes["drag-and-drop"]}
					/>
					<Input
						name="buttonLabel"
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_button_label}
						defaultValue={props.action.buttonLabel ? translateISO369(props.action.buttonLabel) : undefined}
						className={classes["input"]}
					/>
					<Input
						name="buttonLink"
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_button_link}
						defaultValue={props.action.buttonLink}
						className={classes["input"]}
					/>
					<Input
						name="startsAt"
						required
						type="datetime-local"
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_start_date}
						defaultValue={props.action.startsAt ? utcToZonedTime(props.action.startsAt).toISOString().slice(0, 16) : undefined}
						className={classes["input"]}
					/>
					<Input
						name="endsAt"
						required
						type="datetime-local"
						label={I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.action_end_date}
						defaultValue={props.action.endsAt ? utcToZonedTime(props.action.endsAt).toISOString().slice(0, 16) : undefined}
						className={classes["input"]}
					/>
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.actions.edit_action_modal.create_action}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
